import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, FolderPlus, Library, Paperclip, Pencil, Plus, Save, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Chip, Confirm, Empty, Field, FilePickButton, IconBtn, Loading, MediaViewer, Modal, Page, SearchBox, Select } from "../components/wf";
import { api, fileUrl, isImage, uploadFile } from "../lib/api";
import { usePerms } from "../lib/auth";
import { DraftBanner, SaveStatus, useAutoSave, useDraft } from "../lib/autosave";

const useFolders = () => useQuery({ queryKey: ["doc-folders"], queryFn: () => api("/doc-folders") });

function FolderDialog({ folder, onClose }) {
  const qc = useQueryClient();
  const { isAdmin } = usePerms();
  const [name, setName] = useState("");
  useEffect(() => {
    setName(folder && folder !== "new" ? folder.name : "");
  }, [folder]);
  const isNew = folder === "new";
  const done = () => {
    qc.invalidateQueries({ queryKey: ["doc-folders"] });
    qc.invalidateQueries({ queryKey: ["documents"] });
    onClose();
  };
  const save = async () => {
    try {
      await api(isNew ? "/doc-folders" : `/doc-folders/${folder.id}`, { method: isNew ? "POST" : "PUT", body: { name: name.trim() } });
      done();
    } catch (e) {
      toast.error(e.message);
    }
  };
  const remove = async () => {
    try {
      await api(`/doc-folders/${folder.id}`, { method: "DELETE" });
      done();
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <Modal open={!!folder} onClose={onClose} title={isNew ? "New Folder" : "Rename Folder"} testid="folder-dialog">
      <Field label="Folder name" testid="folder-name-input" value={name} onChange={setName} />
      <div className="flex justify-end gap-2">
        {!isNew && isAdmin && <Btn variant="ghost" icon={Trash2} onClick={remove} data-testid="folder-delete">Delete</Btn>}
        <Btn onClick={save} disabled={!name.trim()} data-testid="folder-save">Save</Btn>
      </div>
    </Modal>
  );
}

export function Documentation() {
  const docs = useQuery({ queryKey: ["documents"], queryFn: () => api("/documents") });
  const folders = useFolders();
  const nav = useNavigate();
  const { isManager } = usePerms();
  const [folder, setFolder] = useState("all");
  const [s, setS] = useState("");
  const [editing, setEditing] = useState(null);
  const counts = useMemo(() => {
    const c = { all: docs.data?.length ?? 0 };
    (docs.data ?? []).forEach((d) => (c[d.folder_id ?? ""] = (c[d.folder_id ?? ""] ?? 0) + 1));
    return c;
  }, [docs.data]);
  const data = (docs.data ?? []).filter((d) => (folder === "all" || (d.folder_id ?? "") === folder) && `${d.title} ${d.description}`.toLowerCase().includes(s.toLowerCase()));
  return (
    <Page title="Documentation" subtitle={`${docs.data?.length ?? 0} entries`} wide testid="documentation-page"
      right={<Btn icon={Plus} onClick={() => nav(`/documentation/new${folder !== "all" && folder ? `?folder=${folder}` : ""}`)} data-testid="add-doc-button">New Entry</Btn>}>
      <SearchBox testid="docs-search-input" value={s} onChange={setS} placeholder="Search documentation" />
      <div className="my-4 flex flex-wrap items-center gap-2" data-testid="doc-folders">
        <Chip label={`All (${counts.all})`} selected={folder === "all"} onClick={() => setFolder("all")} testid="doc-folder-all" />
        {(folders.data ?? []).map((f) => (
          <span key={f.id} className="inline-flex items-center">
            <Chip label={`${f.name} (${counts[f.id] ?? 0})`} selected={folder === f.id} onClick={() => setFolder(f.id)} testid={`doc-folder-${f.id}`} />
            {isManager && <button onClick={() => setEditing(f)} data-testid={`doc-folder-edit-${f.id}`} className="ml-0.5 rounded-full p-1.5 text-wf-mute hover:bg-wf-s3"><Pencil size={13} /></button>}
          </span>
        ))}
        {!!counts[""] && <Chip label={`No folder (${counts[""]})`} selected={folder === ""} onClick={() => setFolder("")} testid="doc-folder-none" />}
        {isManager && <Btn size="sm" variant="ghost" icon={FolderPlus} onClick={() => setEditing("new")} data-testid="doc-folder-add">Folder</Btn>}
      </div>
      {docs.isLoading ? <Loading /> : !data.length ? <Empty icon={Library} title="No documentation yet" subtitle="Save wiring diagrams, manuals and how-tos for the whole team." /> : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="docs-list">
          {data.map((d) => (
            <Link key={d.id} to={`/documentation/${d.id}`} data-testid={`doc-card-${d.id}`} className="rounded-2xl border border-wf-line p-5 hover:border-wf-brand2 hover:bg-wf-s2">
              <p className="font-bold">{d.title}</p>
              {d.description && <p className="mt-1 line-clamp-2 text-sm text-wf-ink2">{d.description}</p>}
              <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-wf-mute"><Paperclip size={13} /> {d.files.length} file{d.files.length === 1 ? "" : "s"}
                {d.folder_id && ` · ${folders.data?.find((f) => f.id === d.folder_id)?.name ?? ""}`}</p>
            </Link>
          ))}
        </div>
      )}
      <FolderDialog folder={editing} onClose={() => setEditing(null)} />
    </Page>
  );
}

export function DocForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const folders = useFolders();
  const existing = useQuery({ queryKey: ["document", id], queryFn: () => api(`/documents/${id}`), enabled: !!id });
  const [f, setF] = useState({ title: "", description: "", folder_id: new URLSearchParams(window.location.search).get("folder") ?? "", files: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const d = existing.data;
    if (d && !loaded) {
      setF({ title: d.title, description: d.description, folder_id: d.folder_id ?? "", files: d.files ?? [] });
      setLoaded(true);
    }
  }, [existing.data, loaded]);
  const auto = useAutoSave(f, async (d) => {
    await api(`/documents/${id}`, { method: "PUT", body: { ...d, title: d.title.trim() } });
    qc.invalidateQueries({ queryKey: ["documents"] });
  }, { ready: !!id && loaded, enabled: !!f.title.trim() });
  const draft = useDraft("draft:document:new", f, setF, !id);
  const add = async (files) => {
    setUploading(true);
    try {
      const out = [];
      for (const file of files) out.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, path: await uploadFile(file), name: file.name, mime: file.type || "application/octet-stream" });
      setF((s) => ({ ...s, files: [...s.files, ...out] }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };
  const save = async () => {
    if (!f.title.trim()) return toast.error("Enter a title");
    setSaving(true);
    try {
      await api(id ? `/documents/${id}` : "/documents", { method: id ? "PUT" : "POST", body: { ...f, title: f.title.trim() } });
      if (!id) draft.clear();
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success(id ? "Entry updated" : "Entry added");
      nav("/documentation");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  if (id && existing.isLoading) return <Loading />;
  return (
    <Page title={id ? f.title || "Entry" : "New Entry"} back testid="doc-form"
      right={<>
        {id && <IconBtn icon={Trash2} tone="danger" onClick={() => setConfirm(true)} data-testid="doc-delete-button" />}
        {id ? <SaveStatus status={auto.status} /> : <Btn icon={Save} loading={saving} onClick={save} data-testid="doc-save-button">Save</Btn>}
      </>}>
      <DraftBanner draft={draft} onDiscard={() => { draft.clear(); setF({ title: "", description: "", folder_id: "", files: [] }); }} />
      <div className="max-w-3xl">
        <Field label="Title *" testid="doc-title-input" value={f.title} onChange={(v) => setF((s) => ({ ...s, title: v }))} placeholder="Kastle reader wiring diagram" />
        <Field label="Description" testid="doc-description-input" value={f.description} onChange={(v) => setF((s) => ({ ...s, description: v }))} multiline />
        <Select label="Folder" testid="doc-folder-select" value={f.folder_id} placeholder="No folder" options={(folders.data ?? []).map((x) => ({ value: x.id, label: x.name }))} onChange={(v) => setF((s) => ({ ...s, folder_id: v }))} />
        <div className="mb-3 flex items-center justify-between">
          <p className="text-[13px] font-bold uppercase tracking-[0.08em] text-wf-mute">Files ({f.files.length})</p>
          <FilePickButton testid="doc-add-file" variant="secondary" icon={Paperclip} accept="*/*" multiple loading={uploading} onFiles={add}>Attach files</FilePickButton>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {f.files.map((file, i) => (
            <div key={file.id} className="flex items-center gap-3 rounded-xl border border-wf-line p-3" data-testid={`doc-file-${i}`}>
              {isImage(file.path) ? (
                <button onClick={() => setViewer(file.path)}><img src={fileUrl(file.path)} alt="" className="h-14 w-14 rounded-lg bg-wf-s3 object-cover" /></button>
              ) : <a href={fileUrl(file.path)} target="_blank" rel="noreferrer" className="flex h-14 w-14 items-center justify-center rounded-lg bg-wf-soft text-wf-brand"><FileText size={24} /></a>}
              <a href={fileUrl(file.path)} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm font-semibold hover:underline">{file.name}</a>
              <IconBtn icon={Trash2} tone="danger" onClick={() => setF((s) => ({ ...s, files: s.files.filter((x) => x.id !== file.id) }))} data-testid={`doc-file-remove-${i}`} />
            </div>
          ))}
        </div>
      </div>
      <MediaViewer path={viewer} onClose={() => setViewer(null)} />
      <Confirm open={confirm} title="Delete this entry?" onClose={() => setConfirm(false)}
        onConfirm={async () => { try { await api(`/documents/${id}`, { method: "DELETE" }); qc.invalidateQueries({ queryKey: ["documents"] }); nav("/documentation"); } catch (e) { toast.error(e.message); } }} />
    </Page>
  );
}
